import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
    PiCheckCircleLight,
    PiWarningLight,
} from 'react-icons/pi';
import FieldShell, { fieldSurface, FOCUS_RING_ONLY } from '../shared/TailwindComponents/FieldShell';
import Toggle from '../shared/TailwindComponents/Toggle';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import SingleFileInput from '../shared/TailwindComponents/SingleFileInput';
import Badge from '../shared/TailwindComponents/Badge';

export type AccessLevel = 'view' | 'edit';
export type ShareUser = {
    id: string;
    name: string;
    email: string;
    role: string;
    initials: string;
    avatarUrl: string;
    access?: AccessLevel;
    UserName?: string;
};

type HeadingKey = 'H1' | 'H2' | 'H3' | 'H4' | 'Body';

type TypographyLevel = {
    FontType?: string;
    Size?: number | string;
    Color?: string;
};


type Props = {
    t: (s: string) => string;
    formik: any;
    fontOptions: { label: string; value: string }[];
    headingStyleOptions: { label: string; value: string }[];
    extractedConfidence?: Record<string, string> | null;
};

const isFile = (x: any) => typeof File !== 'undefined' && x instanceof File;
const levelList: HeadingKey[] = ['H1', 'H2', 'H3', 'H4', 'Body'];

const clampNum = (v: any, min: number, max: number) => {
    const n = Number(v);
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
};

/*
  Each section is a grey panel with WHITE controls on it: `surface-canvas`
  remaps the field token to white inside (index.css), and the arbitrary
  variant lifts the off-state Toggle track, which is not a field, to match.
*/
const SECTION_CLASS = clsx(
    'scroll-mt-16 space-y-5 rounded-lg border border-hairline-soft bg-surface-sunken/60 p-6',
    'surface-canvas [&_[role=switch][aria-checked=false]]:bg-surface',
);

/** Section ids, in page order, with their i18n label - the sticky outline reads this. */
export const BRANDING_SECTIONS: { id: string; label: string }[] = [
    { id: 'page-setup', label: 'Page Setup' },
    { id: 'typography', label: 'Typography' },
    { id: 'toc', label: 'Table of Contents' },
    { id: 'heading-numbering', label: 'Heading Numbering' },
    { id: 'margins', label: 'Margins' },
    { id: 'logos', label: 'Logos & Watermark' },
    { id: 'header-footer', label: 'Header & Footer' },
    { id: 'page-numbers', label: 'Page Numbers' },
    { id: 'letterhead', label: 'Letterhead' },
    { id: 'confidentiality', label: 'Confidentiality Notice' },
];
const sectionDomId = (id: string) => `branding-section-${id}`;

/**
 * The sticky outline across the top of the branding editor - ten sections is
 * a long scroll, and this is the map of it. Highlights the section in view.
 */
const BrandingOutlineNav: React.FC<{ t: (s: string) => string }> = ({ t }) => {
    const [activeId, setActiveId] = useState(BRANDING_SECTIONS[0].id);
    const barRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') return undefined;
        const els = BRANDING_SECTIONS
            .map((s) => document.getElementById(sectionDomId(s.id)))
            .filter((el): el is HTMLElement => !!el);
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) setActiveId(visible[0].target.id.replace('branding-section-', ''));
            },
            { rootMargin: '-64px 0px -60% 0px' },
        );
        els.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    // Keep the active pill in view when the bar itself scrolls sideways.
    useEffect(() => {
        const pill = barRef.current?.querySelector<HTMLElement>(`[data-section="${activeId}"]`);
        pill?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }, [activeId]);

    return (
        <nav
            ref={barRef}
            aria-label={t('Sections')}
            className="glass-bar sticky top-0 z-20 -mx-2 flex gap-1 overflow-x-auto rounded-lg border border-hairline-soft px-2 py-1.5 custom-scrollbar-main"
        >
            {BRANDING_SECTIONS.map((s) => (
                <button
                    key={s.id}
                    type="button"
                    data-section={s.id}
                    aria-current={activeId === s.id ? 'true' : undefined}
                    onClick={() => {
                        setActiveId(s.id);
                        document.getElementById(sectionDomId(s.id))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className={clsx(
                        'nav-pill flex-shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm',
                        activeId === s.id ? 'nav-pill-active text-fg-strong' : 'text-fg-muted hover:text-fg-body',
                    )}
                >
                    {t(s.label)}
                </button>
            ))}
        </nav>
    );
};

const WORD_POINT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

const BrandingPanel: React.FC<Props> = ({
    t,
    formik,
    fontOptions,
    headingStyleOptions,
    extractedConfidence,
}) => {

    // Confidence badge helper for extracted sections
    const renderConfidenceBadge = (section: string) => {
        if (!extractedConfidence) return null;
        const level = extractedConfidence[section];
        if (!level) return null;
        const colors: Record<string, 'green' | 'yellow' | 'gray'> = {
            high: 'green',
            medium: 'yellow',
            low: 'gray'
        };
        return (
            <Badge color={colors[level] || 'gray'} className="ms-2 text-xs">
                {level === 'high' ? <PiCheckCircleLight className="inline me-1" /> : level === 'low' ? <PiWarningLight className="inline me-1" /> : null}
                {level}
            </Badge>
        );
    };

    const globalBodySize = formik.values.Branding.Typography.DocumentFontSize ?? 12;
    const globalHeadingSize = formik.values.Branding.Typography.HeadingFontSize || 'auto';

    const levels: Record<HeadingKey, TypographyLevel> = useMemo(() => {
        const lv = formik.values.Branding.Typography.Levels || {};
        const ensure = (k: HeadingKey): TypographyLevel => lv[k] || {};
        return {
            H1: ensure('H1'),
            H2: ensure('H2'),
            H3: ensure('H3'),
            H4: ensure('H4'),
            Body: ensure('Body'),
        };
    }, [formik.values.Branding?.Typography?.Levels]);

    const setLevel = (k: HeadingKey, path: keyof TypographyLevel, value: any) => {
        formik.setFieldValue(`Branding.Typography.Levels.${k}.${path}`, value);
    };

    const fallbackHeadingSize = (k: HeadingKey) => {
        if (k === 'Body') return globalBodySize;
        if (globalHeadingSize !== 'auto' && globalHeadingSize !== '' && !Number.isNaN(Number(globalHeadingSize))) {
            return Number(globalHeadingSize);
        }
        // auto ladder
        switch (k) {
            case 'H1': return 18;
            case 'H2': return 16;
            case 'H3': return 14;
            case 'H4': return 12.5;
            default: return globalBodySize;
        }
    };

    return (
        <div className="space-y-6">
            <BrandingOutlineNav t={t} />
            {/* Page Setup Section */}
            <section id={sectionDomId('page-setup')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Page setup')}{renderConfidenceBadge('page_setup')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Paper size and first-page behavior for the generated document.')}
                    </p>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Dropdown
                        value={formik.values.Branding.PaperSize ?? 'A4'}
                        onChange={(value) => formik.setFieldValue('Branding.PaperSize', value)}
                        options={[
                            { label: t('A4 (210 × 297 mm)'), value: 'A4' },
                            { label: t('Letter (8.5 × 11 in)'), value: 'Letter' },
                        ]}
                        label={t('Paper size')}
                    />
                    <div className="flex items-end">
                        <Toggle
                            enabled={formik.values.Branding.FirstPageDifferent ?? false}
                            onToggle={(value) => formik.setFieldValue('Branding.FirstPageDifferent', value)}
                            label={t('Different first page (header/footer)')}
                            translate={false}
                        />
                    </div>
                </div>
            </section>

            <section id={sectionDomId('typography')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Typography')}{renderConfidenceBadge('typography')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Set global defaults, then override per level (H1–H4, Body) as needed.')}
                    </p>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Dropdown
                        value={formik.values.Branding.Typography.FontType}
                        onChange={(value) => formik.setFieldValue('Branding.Typography.FontType', value)}
                        options={fontOptions}
                        label={t('Default font')}
                    />
                    <Input
                        type="number"
                        name="DocumentFontSize"
                        value={formik.values.Branding.Typography.DocumentFontSize}
                        onChange={(e) =>
                            formik.setFieldValue('Branding.Typography.DocumentFontSize', clampNum(e.target.value, 8, 20))
                        }
                        label={t('Default body size (pt)')}
                        infoText={t('Between 8 and 20')}
                        required
                        className="mt-3 w-50"
                    />
                    <Input
                        type="text"
                        name="HeadingFontSize"
                        value={formik.values.Branding.Typography.HeadingFontSize}
                        onChange={(e) => {
                            const raw = e.target.value.trim();
                            if (raw.toLowerCase() === 'auto' || raw === '') {
                                formik.setFieldValue('Branding.Typography.HeadingFontSize', 'auto');
                            } else {
                                formik.setFieldValue('Branding.Typography.HeadingFontSize', clampNum(raw, 10, 24));
                            }
                        }}
                        label={t('Default heading size')}
                        helperText={t('Enter "auto" or a number')}
                        required
                        className="mt-3 w-50"
                    />
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Dropdown
                        value={String(formik.values.Branding.Typography.LineSpacing ?? 1.5)}
                        onChange={(value) => formik.setFieldValue('Branding.Typography.LineSpacing', Number(value))}
                        options={[
                            { label: t('Single (1.0)'), value: '1' },
                            { label: t('1.15'), value: '1.15' },
                            { label: t('1.5'), value: '1.5' },
                            { label: t('Double (2.0)'), value: '2' },
                        ]}
                        label={t('Line spacing')}
                    />
                    <Dropdown
                        value={formik.values.Branding.Typography.Alignment ?? 'left'}
                        onChange={(value) => formik.setFieldValue('Branding.Typography.Alignment', value)}
                        options={[
                            { label: t('Left'), value: 'left' },
                            { label: t('Justified'), value: 'justify' },
                            { label: t('Right'), value: 'right' },
                            { label: t('Center'), value: 'center' },
                        ]}
                        label={t('Body alignment')}
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <Input
                            type="number"
                            name="ParagraphSpacingBefore"
                            value={formik.values.Branding.Typography.ParagraphSpacingBefore ?? 0}
                            onChange={(e) =>
                                formik.setFieldValue('Branding.Typography.ParagraphSpacingBefore', clampNum(e.target.value, 0, 48))
                            }
                            label={t('Space before (pt)')}
                        />
                        <Input
                            type="number"
                            name="ParagraphSpacingAfter"
                            value={formik.values.Branding.Typography.ParagraphSpacingAfter ?? 6}
                            onChange={(e) =>
                                formik.setFieldValue('Branding.Typography.ParagraphSpacingAfter', clampNum(e.target.value, 0, 48))
                            }
                            label={t('Space after (pt)')}
                        />
                    </div>
                </div>

                <div className="mt-6 space-y-4">
                    {levelList.map((level) => {
                        const l = levels[level] || {};
                        const label = level === 'Body' ? t('Body (normal text)') : level;
                        const sizeValue =
                            typeof l.Size === 'number'
                                ? String(l.Size)
                                : (typeof l.Size === 'string' && l.Size.toLowerCase() === 'auto') ? 'auto' : '';

                        return (
                            <div key={level} className="py-4 border-b border-hairline-soft last:border-b-0">
                                <h5 className="text-sm text-fg-strong mb-3">{label}</h5>
                                <div className="grid grid-cols-3 gap-4">
                                    <Dropdown
                                        value={l.FontType ?? ''}
                                        onChange={(value) => setLevel(level, 'FontType', value)}
                                        options={[{ label: t('Use default'), value: '' }, ...fontOptions]}
                                        label={t('Font')}
                                        fullWidth
                                    />

                                    <Dropdown
                                        value={sizeValue}
                                        onChange={(value) => {
                                            const raw = value;
                                            if (raw === '' || raw === 'auto') {
                                                setLevel(level, 'Size', 'auto');
                                            } else {
                                                setLevel(level, 'Size', clampNum(raw, 8, 30));
                                            }
                                        }}
                                        options={WORD_POINT_SIZES.map((pt) => ({ label: `${pt} pt`, value: `${pt}` }))}
                                        label={t('Size (pt)')}
                                        fullWidth
                                    />

                                    <FieldShell id={`${level}-color-hex`} label={t('Color')} translate={false}>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={l.Color || '#000000'}
                                                onChange={(e) => setLevel(level, 'Color', e.target.value)}
                                                className={clsx(
                                                    'w-9 h-9 flex-shrink-0 rounded-md border border-hairline cursor-pointer p-0 bg-transparent',
                                                    FOCUS_RING_ONLY
                                                )}
                                                aria-label={t('Color')}
                                                title={l.Color || t('Default')}
                                            />
                                            <input
                                                id={`${level}-color-hex`}
                                                type="text"
                                                value={l.Color || ''}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (v === '' || /^#([0-9a-fA-F]{0,6})$/.test(v)) {
                                                        setLevel(level, 'Color', v);
                                                    }
                                                }}
                                                placeholder={t('Default')}
                                                className={clsx(fieldSurface(), 'px-3 py-1.5')}
                                            />
                                        </div>
                                    </FieldShell>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Table of Contents Section */}
            <section id={sectionDomId('toc')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Table of contents')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Auto-generated table of contents for longer documents. Standard in legal agreements over 5 pages.')}
                    </p>
                </div>
                <Toggle
                    enabled={formik.values.Branding.TableOfContents?.Enabled ?? false}
                    onToggle={(value) => formik.setFieldValue('Branding.TableOfContents.Enabled', value)}
                    label={t('Include table of contents')}
                    translate={false}
                />
                {formik.values.Branding.TableOfContents?.Enabled && (
                    <div className="grid grid-cols-2 gap-4">
                        <Dropdown
                            value={String(formik.values.Branding.TableOfContents?.Depth ?? 3)}
                            onChange={(value) => formik.setFieldValue('Branding.TableOfContents.Depth', Number(value))}
                            options={[
                                { label: t('H1 only'), value: '1' },
                                { label: t('H1 + H2'), value: '2' },
                                { label: t('H1 + H2 + H3'), value: '3' },
                                { label: t('All headings'), value: '4' },
                            ]}
                            label={t('Depth')}
                        />
                        <Dropdown
                            value={formik.values.Branding.TableOfContents?.Style ?? 'dotted'}
                            onChange={(value) => formik.setFieldValue('Branding.TableOfContents.Style', value)}
                            options={[
                                { label: t('Dotted leaders'), value: 'dotted' },
                                { label: t('Dashed leaders'), value: 'dashed' },
                                { label: t('No leaders'), value: 'none' },
                            ]}
                            label={t('Style')}
                        />
                    </div>
                )}
            </section>

            {/* Heading Numbering Section */}
            <section id={sectionDomId('heading-numbering')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Heading numbering')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Automatic numbering of headings, common in legal agreements (e.g. 1, 1.1, 1.1.1).')}
                    </p>
                </div>
                <Dropdown
                    value={formik.values.Branding.HeadingNumbering?.Style ?? 'none'}
                    onChange={(value) => formik.setFieldValue('Branding.HeadingNumbering.Style', value)}
                    options={[
                        { label: t('None'), value: 'none' },
                        { label: t('Decimal (1, 2, 3)'), value: 'decimal' },
                        { label: t('Legal (1, 1.1, 1.1.1)'), value: 'legal' },
                    ]}
                    label={t('Numbering style')}
                />
            </section>

            <section id={sectionDomId('margins')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Margins')}{renderConfidenceBadge('margins')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Standardized margins ensure consistent pagination and printer-friendly PDFs.')}
                    </p>
                </div>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {(['Top', 'Bottom', 'Left', 'Right'] as const).map((side) => (
                        <Input
                            key={side}
                            type="number"
                            name={side}
                            value={formik.values.Branding.Margins[side]}
                            onChange={(e) => formik.setFieldValue(`Branding.Margins.${side}`, parseFloat(e.target.value))}
                            label={t(side)}
                            helperText={t('In inches')}
                            required
                        />
                    ))}
                </div>
            </section>

            <section id={sectionDomId('logos')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Logos & watermark')}{renderConfidenceBadge('logos')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Add firm branding and optional watermark for draft/confidential documents.')}
                    </p>
                </div>

                <div className="mt-4 space-y-4">
                    <Toggle
                        enabled={formik.values.Branding.Logos.UseFirmLogo}
                        onToggle={(value) => formik.setFieldValue('Branding.Logos.UseFirmLogo', value)}
                        label={t('Use firm logo')}
                        translate={false}
                    />

                    {formik.values.Branding.Logos.UseFirmLogo && (
                        <div className="grid grid-cols-2 gap-4">
                            <SingleFileInput
                                label={t('Firm logo')}
                                accept="image/*"
                                file={isFile(formik.values.Branding.Logos.FirmLogo) ? formik.values.Branding.Logos.FirmLogo : null}
                                setFile={(file) => formik.setFieldValue('Branding.Logos.FirmLogo', file)}
                                existingFileUrl={
                                    typeof formik.values.Branding.Logos.FirmLogo === 'string'
                                        ? formik.values.Branding.Logos.FirmLogo
                                        : undefined
                                }
                                desc={t('Used in headers/footers and cover pages')}
                            />
                            <div className="space-y-4">
                                <Toggle
                                    enabled={formik.values.Branding.Logos.Color}
                                    onToggle={(value) => formik.setFieldValue('Branding.Logos.Color', value)}
                                    label={t('Colored logo')}
                                    translate={false}
                                />
                                <Input
                                    type="number"
                                    name="FirmLogoSize"
                                    value={formik.values.Branding.Logos.FirmLogoSize ?? 24}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.Logos.FirmLogoSize', clampNum(e.target.value, 12, 96))
                                    }
                                    label={t('Logo size (pt)')}
                                    infoText={t('12–96pt')}
                                />
                            </div>
                        </div>
                    )}

                    <Toggle
                        enabled={!!formik.values.Branding.Logos.UseWatermark}
                        onToggle={(value) => formik.setFieldValue('Branding.Logos.UseWatermark', value)}
                        label={t('Use watermark')}
                        translate={false}
                    />

                    {formik.values.Branding.Logos.UseWatermark && (
                        <div className="grid grid-cols-2 gap-4">
                            <SingleFileInput
                                label={t('Watermark logo')}
                                accept="image/*"
                                file={
                                    isFile(formik.values.Branding.Logos.WatermarkLogo)
                                        ? formik.values.Branding.Logos.WatermarkLogo
                                        : null
                                }
                                setFile={(file) => formik.setFieldValue('Branding.Logos.WatermarkLogo', file)}
                                existingFileUrl={
                                    typeof formik.values.Branding.Logos.WatermarkLogo === 'string'
                                        ? formik.values.Branding.Logos.WatermarkLogo
                                        : undefined
                                }
                                desc={t('Optional logo applied with opacity')}
                            />
                            <div className="space-y-4">
                                <Input
                                    name="WatermarkText"
                                    value={formik.values.Branding.Logos.WatermarkText ?? ''}
                                    onChange={(e) => formik.setFieldValue('Branding.Logos.WatermarkText', e.target.value)}
                                    label={t('Watermark text')}
                                    infoText={t('Used if no watermark logo (e.g., DRAFT, CONFIDENTIAL)')}
                                    type="text"
                                />
                                <Input
                                    type="number"
                                    name="WatermarkOpacity"
                                    value={formik.values.Branding.Logos.WatermarkOpacity ?? 25}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.Logos.WatermarkOpacity', clampNum(e.target.value, 10, 50))
                                    }
                                    label={t('Watermark opacity (%)')}
                                    infoText={t('10–50%. Default 25%')}
                                    required
                                />
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section id={sectionDomId('header-footer')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Header & footer')}{renderConfidenceBadge('header_footer')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Configure page headers and footers with up to three text positions (left, center, right) and optional logos.')}
                    </p>
                </div>

                <div className="mt-4 space-y-6">
                    {/* Header Section */}
                    <div>
                        <h5 className="text-sm text-fg-strong mb-3">{t('Header')}</h5>
                        
                        {/* Per-position mode selectors */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            {(['Left', 'Center', 'Right'] as const).map((position) => {
                                const modeKey = `Header${position}Mode` as const;
                                const textKey = `HeaderText${position}` as const;
                                const imageKey = `Header${position}Image` as const;
                                const mode = formik.values.Branding.HeaderFooter[modeKey] ?? 'none';
                                
                                return (
                                    <div key={position} className="space-y-2">
                                        <Dropdown
                                            value={mode}
                                            onChange={(value) => formik.setFieldValue(`Branding.HeaderFooter.${modeKey}`, value)}
                                            options={[
                                                { label: t('None'), value: 'none' },
                                                { label: t('Text'), value: 'text' },
                                                { label: t('Logo/Image'), value: 'logo' },
                                            ]}
                                            label={t(position)}
                                        />
                                        {mode === 'text' && (
                                            <Input
                                                name={textKey}
                                                value={formik.values.Branding.HeaderFooter[textKey] ?? ''}
                                                onChange={(e) => formik.setFieldValue(`Branding.HeaderFooter.${textKey}`, e.target.value)}
                                                placeholder={position === 'Left' ? t('e.g., Firm Name') : position === 'Center' ? t('e.g., Document Title') : t('e.g., Date')}
                                                type="text"
                                            />
                                        )}
                                        {mode === 'logo' && (
                                            <SingleFileInput
                                                accept="image/*"
                                                file={isFile(formik.values.Branding.HeaderFooter[imageKey]) ? formik.values.Branding.HeaderFooter[imageKey] as File : null}
                                                setFile={(file) => formik.setFieldValue(`Branding.HeaderFooter.${imageKey}`, file)}
                                                existingFileUrl={
                                                    typeof formik.values.Branding.HeaderFooter[imageKey] === 'string' && formik.values.Branding.HeaderFooter[imageKey]
                                                        ? formik.values.Branding.HeaderFooter[imageKey] as string
                                                        : undefined
                                                }
                                                desc={t('Upload image')}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Header font settings - show if any position has text */}
                        {(formik.values.Branding.HeaderFooter.HeaderLeftMode === 'text' ||
                            formik.values.Branding.HeaderFooter.HeaderCenterMode === 'text' ||
                            formik.values.Branding.HeaderFooter.HeaderRightMode === 'text') && (
                            <div className="grid grid-cols-2 gap-4">
                                <Dropdown
                                    value={formik.values.Branding.HeaderFooter.HeaderFontType ?? ''}
                                    onChange={(value) => formik.setFieldValue('Branding.HeaderFooter.HeaderFontType', value)}
                                    options={[{ label: t('Use default'), value: '' }, ...fontOptions]}
                                    label={t('Header font')}
                                />
                                <Input
                                    type="number"
                                    name="HeaderFontSize"
                                    value={formik.values.Branding.HeaderFooter.HeaderFontSize ?? 10}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.HeaderFooter.HeaderFontSize', clampNum(e.target.value, 8, 20))
                                    }
                                    label={t('Font size (pt)')}
                                />
                            </div>
                        )}

                        {/* Header logo size - show if any position has logo */}
                        {(formik.values.Branding.HeaderFooter.HeaderLeftMode === 'logo' ||
                            formik.values.Branding.HeaderFooter.HeaderCenterMode === 'logo' ||
                            formik.values.Branding.HeaderFooter.HeaderRightMode === 'logo') && (
                            <div className="mt-3">
                                <Input
                                    type="number"
                                    name="HeaderLogoSize"
                                    value={formik.values.Branding.HeaderFooter.HeaderLogoSize ?? 20}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.HeaderFooter.HeaderLogoSize', clampNum(e.target.value, 12, 96))
                                    }
                                    label={t('Logo size (pt)')}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer Section */}
                    <div>
                        <h5 className="text-sm text-fg-strong mb-3">{t('Footer')}</h5>
                        
                        {/* Per-position mode selectors */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            {(['Left', 'Center', 'Right'] as const).map((position) => {
                                const modeKey = `Footer${position}Mode` as const;
                                const textKey = `FooterText${position}` as const;
                                const imageKey = `Footer${position}Image` as const;
                                const mode = formik.values.Branding.HeaderFooter[modeKey] ?? 'none';
                                
                                return (
                                    <div key={position} className="space-y-2">
                                        <Dropdown
                                            value={mode}
                                            onChange={(value) => formik.setFieldValue(`Branding.HeaderFooter.${modeKey}`, value)}
                                            options={[
                                                { label: t('None'), value: 'none' },
                                                { label: t('Text'), value: 'text' },
                                                { label: t('Logo/Image'), value: 'logo' },
                                            ]}
                                            label={t(position)}
                                        />
                                        {mode === 'text' && (
                                            <Input
                                                name={textKey}
                                                value={formik.values.Branding.HeaderFooter[textKey] ?? ''}
                                                onChange={(e) => formik.setFieldValue(`Branding.HeaderFooter.${textKey}`, e.target.value)}
                                                placeholder={position === 'Left' ? t('e.g., Confidential') : position === 'Center' ? t('e.g., {page}') : t('e.g., Page {page}')}
                                                type="text"
                                            />
                                        )}
                                        {mode === 'logo' && (
                                            <SingleFileInput
                                                accept="image/*"
                                                file={isFile(formik.values.Branding.HeaderFooter[imageKey]) ? formik.values.Branding.HeaderFooter[imageKey] as File : null}
                                                setFile={(file) => formik.setFieldValue(`Branding.HeaderFooter.${imageKey}`, file)}
                                                existingFileUrl={
                                                    typeof formik.values.Branding.HeaderFooter[imageKey] === 'string' && formik.values.Branding.HeaderFooter[imageKey]
                                                        ? formik.values.Branding.HeaderFooter[imageKey] as string
                                                        : undefined
                                                }
                                                desc={t('Upload image')}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer font settings - show if any position has text */}
                        {(formik.values.Branding.HeaderFooter.FooterLeftMode === 'text' ||
                            formik.values.Branding.HeaderFooter.FooterCenterMode === 'text' ||
                            formik.values.Branding.HeaderFooter.FooterRightMode === 'text') && (
                            <div className="grid grid-cols-2 gap-4">
                                <Dropdown
                                    value={formik.values.Branding.HeaderFooter.FooterFontType ?? ''}
                                    onChange={(value) => formik.setFieldValue('Branding.HeaderFooter.FooterFontType', value)}
                                    options={[{ label: t('Use default'), value: '' }, ...fontOptions]}
                                    label={t('Footer font')}
                                />
                                <Input
                                    type="number"
                                    name="FooterFontSize"
                                    value={formik.values.Branding.HeaderFooter.FooterFontSize ?? 10}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.HeaderFooter.FooterFontSize', clampNum(e.target.value, 8, 20))
                                    }
                                    label={t('Font size (pt)')}
                                />
                            </div>
                        )}

                        {/* Footer logo size - show if any position has logo */}
                        {(formik.values.Branding.HeaderFooter.FooterLeftMode === 'logo' ||
                            formik.values.Branding.HeaderFooter.FooterCenterMode === 'logo' ||
                            formik.values.Branding.HeaderFooter.FooterRightMode === 'logo') && (
                            <div className="mt-3">
                                <Input
                                    type="number"
                                    name="FooterLogoSize"
                                    value={formik.values.Branding.HeaderFooter.FooterLogoSize ?? 18}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.HeaderFooter.FooterLogoSize', clampNum(e.target.value, 12, 96))
                                    }
                                    label={t('Logo size (pt)')}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Page Numbers Section */}
            <section id={sectionDomId('page-numbers')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Page numbers')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Automatic page numbering, inserted as live Word fields that update on print.')}
                    </p>
                </div>
                <Toggle
                    enabled={formik.values.Branding.PageNumber?.Enabled ?? false}
                    onToggle={(value) => formik.setFieldValue('Branding.PageNumber.Enabled', value)}
                    label={t('Include page numbers')}
                    translate={false}
                />
                {formik.values.Branding.PageNumber?.Enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Dropdown
                            value={formik.values.Branding.PageNumber?.Format ?? 'Page X of Y'}
                            onChange={(value) => formik.setFieldValue('Branding.PageNumber.Format', value)}
                            options={[
                                { label: t('1'), value: 'X' },
                                { label: t('1 of N'), value: 'X of Y' },
                                { label: t('Page 1'), value: 'Page X' },
                                { label: t('Page 1 of N'), value: 'Page X of Y' },
                            ]}
                            label={t('Format')}
                        />
                        <Dropdown
                            value={formik.values.Branding.PageNumber?.Position ?? 'footer-center'}
                            onChange={(value) => formik.setFieldValue('Branding.PageNumber.Position', value)}
                            options={[
                                { label: t('Footer — left'), value: 'footer-left' },
                                { label: t('Footer — center'), value: 'footer-center' },
                                { label: t('Footer — right'), value: 'footer-right' },
                                { label: t('Header — left'), value: 'header-left' },
                                { label: t('Header — center'), value: 'header-center' },
                                { label: t('Header — right'), value: 'header-right' },
                            ]}
                            label={t('Position')}
                        />
                        <Input
                            type="number"
                            name="PageNumberStartAt"
                            value={formik.values.Branding.PageNumber?.StartAt ?? 1}
                            onChange={(e) =>
                                formik.setFieldValue('Branding.PageNumber.StartAt', clampNum(e.target.value, 0, 999))
                            }
                            label={t('Start at')}
                        />
                    </div>
                )}
            </section>

            {/* Letterhead Section */}
            <section id={sectionDomId('letterhead')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Letterhead')}{renderConfidenceBadge('letterhead')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('Firm identity block placed at the top of the first page.')}
                    </p>
                </div>
                <Toggle
                    enabled={formik.values.Branding.Letterhead?.Enabled ?? false}
                    onToggle={(value) => formik.setFieldValue('Branding.Letterhead.Enabled', value)}
                    label={t('Include letterhead')}
                    translate={false}
                />
                {formik.values.Branding.Letterhead?.Enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Input
                            type="text"
                            name="LetterheadFirmName"
                            value={formik.values.Branding.Letterhead?.FirmName ?? ''}
                            onChange={(e) => formik.setFieldValue('Branding.Letterhead.FirmName', e.target.value)}
                            label={t('Firm name')}
                        />
                        <Input
                            type="text"
                            name="LetterheadOrgNumber"
                            value={formik.values.Branding.Letterhead?.OrgNumber ?? ''}
                            onChange={(e) => formik.setFieldValue('Branding.Letterhead.OrgNumber', e.target.value)}
                            label={t('Org. Number')}
                        />
                        <FieldShell id="letterhead-address" label={t('Address')} translate={false}>
                            <textarea
                                id="letterhead-address"
                                rows={3}
                                value={formik.values.Branding.Letterhead?.AddressLines ?? ''}
                                onChange={(e) => formik.setFieldValue('Branding.Letterhead.AddressLines', e.target.value)}
                                placeholder={t('One line per row')}
                                className={clsx(fieldSurface(), 'px-3 py-2 w-full resize-y')}
                            />
                        </FieldShell>
                        <Input
                            type="text"
                            name="LetterheadContact"
                            value={formik.values.Branding.Letterhead?.Contact ?? ''}
                            onChange={(e) => formik.setFieldValue('Branding.Letterhead.Contact', e.target.value)}
                            label={t('Contact (phone / email / web)')}
                        />
                    </div>
                )}
            </section>

            {/* Confidentiality Section */}
            <section id={sectionDomId('confidentiality')} className={SECTION_CLASS}>
                <div>
                    <h4 className="text-base font-medium text-fg-strong flex items-center">{t('Confidentiality notice')}</h4>
                    <p className="text-sm text-fg-muted">
                        {t('A confidentiality or privilege statement placed on the document.')}
                    </p>
                </div>
                <Toggle
                    enabled={formik.values.Branding.Confidentiality?.Enabled ?? false}
                    onToggle={(value) => formik.setFieldValue('Branding.Confidentiality.Enabled', value)}
                    label={t('Include confidentiality notice')}
                    translate={false}
                />
                {formik.values.Branding.Confidentiality?.Enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Input
                            type="text"
                            name="ConfidentialityText"
                            value={formik.values.Branding.Confidentiality?.Text ?? ''}
                            onChange={(e) => formik.setFieldValue('Branding.Confidentiality.Text', e.target.value)}
                            label={t('Notice text')}
                            placeholder={t('e.g., PRIVILEGED & CONFIDENTIAL')}
                        />
                        <Dropdown
                            value={formik.values.Branding.Confidentiality?.Placement ?? 'first-page'}
                            onChange={(value) => formik.setFieldValue('Branding.Confidentiality.Placement', value)}
                            options={[
                                { label: t('Top of first page'), value: 'first-page' },
                                { label: t('Header (all pages)'), value: 'header' },
                                { label: t('Footer (all pages)'), value: 'footer' },
                            ]}
                            label={t('Placement')}
                        />
                    </div>
                )}
            </section>
        </div>
    );
};

export default BrandingPanel;
